import React, { useEffect, useState } from 'react';
import { getPendingDeploymentsForYear, deployApprovalRequest, deployModule, uploadModuleEnrollmentForYear, getPendingApprovalRequests, handleApprovalAction, getSystemSettings, getModuleEnrollments, saveModuleEnrollmentForYear, getPendingDeploymentYears } from '../../api/adminApi';
import ApprovalRequestCard from '../../components/ui/ApprovalRequestCard';
import RosterEditorModal from '../../components/ui/RosterEditorModal';

const PendingDeployments = ({ user }) => {
  const [items, setItems] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewYear, setViewYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [academicYearStartDate, setAcademicYearStartDate] = useState({ month: 9, day: 22 });
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [isRosterEditorOpen, setIsRosterEditorOpen] = useState(false);
  const [rosterEditorRows, setRosterEditorRows] = useState([]);
  const [rosterEditorTarget, setRosterEditorTarget] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterSaving, setRosterSaving] = useState(false);

  const MOCK_ADMIN_ID = 1;

  const getCurrentAcademicYearStart = (startMonth, startDay, referenceDate = new Date()) => {
    const currentYear = referenceDate.getFullYear();
    const currentMonth = referenceDate.getMonth() + 1;
    const currentDay = referenceDate.getDate();

    if (currentMonth < startMonth || (currentMonth === startMonth && currentDay < startDay)) {
      return currentYear - 1;
    }

    return currentYear;
  };

  const fetchItems = async (targetYear) => {
    setLoading(true);
    setError(null);
    try {
      let res = [];
      if (targetYear) {
        res = await getPendingDeploymentsForYear(targetYear);
      }
      setItems(res || []);
      // also fetch pending approval requests (dashboard merged)
      try {
        const apr = await getPendingApprovalRequests();
        setApprovals(apr || []);
      } catch (aerr) {
        console.error('Failed to fetch approvals:', aerr);
      }
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const years = await getPendingDeploymentYears();
      const sortedYears = (years || []).slice().sort((a, b) => b - a);
      setAvailableYears(sortedYears);
    } catch (err) {
      console.error('Failed to fetch pending deployment years:', err);
      setAvailableYears([]);
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getSystemSettings();
        if (settings && settings.academic_year_start_date) {
          const [m, d] = settings.academic_year_start_date.split('-').map(Number);
          if (Number.isInteger(m) && Number.isInteger(d)) {
            setAcademicYearStartDate({ month: m, day: d });
            const yearStart = getCurrentAcademicYearStart(m, d);
            setCurrentAcademicYear(yearStart);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to load system settings:', err);
      }
      const fallbackYear = getCurrentAcademicYearStart(academicYearStartDate.month, academicYearStartDate.day);
      setCurrentAcademicYear(fallbackYear);
    };

    loadSettings();
  }, []);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    if (!currentAcademicYear) return;

    if (availableYears.length === 0) {
      if (viewYear !== null) setViewYear(null);
      return;
    }

    const hasCurrent = viewYear && availableYears.includes(viewYear);
    if (hasCurrent) return;

    const preferredYear = availableYears.includes(currentAcademicYear)
      ? currentAcademicYear
      : availableYears[0];

    if (viewYear !== preferredYear) {
      setViewYear(preferredYear);
    }
  }, [availableYears, currentAcademicYear, viewYear]);

  useEffect(() => {
    if (viewYear !== null) {
      fetchItems(viewYear);
    } else {
      setItems([]);
    }
  }, [viewYear]);

  const getAcademicYear = (date = new Date()) => {
    const y = date.getFullYear();
    const m = date.getMonth() + 1; // 1-12
    return m >= 9 ? y : y - 1;
  };

  const handleDeploy = async (item) => {
    // item may be an approved request (with request_id) or a plain module (no request_id)
    setActionLoading(item.request_id || item.module_id);
    setError(null);
    try {
      let res;
      // determine target year: prefer selected viewYear, then this item's latest enrollment year, then current year
      const itemYears = item.enrollment_years && item.enrollment_years.length > 0
        ? item.enrollment_years.map(e => e.year).sort((a, b) => b - a)
        : [];
      let targetYear = viewYear || (itemYears.length ? itemYears[0] : currentAcademicYear);

      if (item.request_id) {
        // deploy approval request for the targetYear if provided
        res = await deployApprovalRequest(item.request_id, user?.id, targetYear);
      } else {
        res = await deployModule(item.module_id, user?.id, targetYear);
      }
      const yearLabel = targetYear ? ` for ${targetYear}-${targetYear + 1}` : '';
      const moduleLabel = item.module_name ? `${item.module_name} (ID: ${item.module_id})` : `module ${item.module_id}`;
      const fallbackMessage = `Deployed survey for ${moduleLabel}${yearLabel}.`;
      alert(res.message || fallbackMessage);
      fetchItems(viewYear || currentAcademicYear);
      fetchAvailableYears();
    } catch (err) {
      setError(err.message || 'Deploy failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFileUpload = async (moduleId, file) => {
    setError(null);
    try {
      const res = await uploadModuleEnrollmentForYear(moduleId, file, viewYear || currentAcademicYear);
      alert(`Uploaded: ${res.students_processed} students`);
      fetchItems(viewYear || currentAcademicYear);
      fetchAvailableYears();
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  const openRosterEditor = async (item) => {
    const itemYears = item.enrollment_years && item.enrollment_years.length > 0
      ? item.enrollment_years.map(e => e.year).sort((a, b) => b - a)
      : [];
    const targetYear = viewYear || (itemYears.length ? itemYears[0] : currentAcademicYear);
    if (!targetYear) {
      setError('No target academic year selected');
      return;
    }

    setError(null);
    setRosterLoading(true);
    try {
      const res = await getModuleEnrollments(item.module_id, targetYear);
      setRosterEditorRows(res.rows || []);
      setRosterEditorTarget({
        moduleId: item.module_id,
        moduleName: item.module_name,
        year: targetYear,
      });
      setIsRosterEditorOpen(true);
    } catch (err) {
      setError(err.message || 'Failed to load roster');
    } finally {
      setRosterLoading(false);
    }
  };

  const handleSaveRoster = async (students) => {
    if (!rosterEditorTarget) return;

    setRosterSaving(true);
    setError(null);
    try {
      const res = await saveModuleEnrollmentForYear(
        rosterEditorTarget.moduleId,
        rosterEditorTarget.year,
        students,
      );
      alert(`Roster saved. Students processed: ${res.students_processed}`);
      setIsRosterEditorOpen(false);
      fetchItems(viewYear || currentAcademicYear);
      fetchAvailableYears();
    } catch (err) {
      setError(err.message || 'Failed to save roster');
    } finally {
      setRosterSaving(false);
    }
  };

  const handleApprovalActionLocal = async (requestId, action, feedback) => {
    setActionLoading(requestId);
    try {
      await handleApprovalAction(requestId, action, MOCK_ADMIN_ID, feedback);
      alert(`Request ${action}ed.`);
      fetchAvailableYears();
      fetchItems(viewYear);
    } catch (err) {
      setError(err.message || 'Approval action failed');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-indigo-700">Pending Deployments</h2>
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="text-sm text-gray-700">
          Current academic year: <span className="font-semibold">{currentAcademicYear ? `${currentAcademicYear}-${currentAcademicYear + 1}` : 'Loading...'}</span>
        </div>
        <label className="font-semibold text-gray-700" htmlFor="pending-year">View year:</label>
        <select
          id="pending-year"
          value={viewYear || ''}
          onChange={(e) => setViewYear(e.target.value ? Number(e.target.value) : null)}
          className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          {availableYears.length === 0 ? <option value="">No pending years</option> : null}
          {availableYears.map(year => (
            <option key={year} value={year}>{year}-{year + 1}</option>
          ))}
        </select>
      </div>
      {loading ? <div>Loading...</div> : null}
      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      <div className="space-y-4">
        {/* --- Approval Requests (merged dashboard) --- */}
        {approvals.length === 0 ? null : (
          <div className="mb-6">
            <h3 className="text-2xl font-semibold text-indigo-700 mb-3">Pending Approval Requests</h3>
            <div className="space-y-4">
              {approvals.map(req => (
                <ApprovalRequestCard key={`req-${req.request_id}`} request={req} onApprove={(id) => handleApprovalActionLocal(id, 'approve')} onDeny={(id, act, feedback) => handleApprovalActionLocal(id, 'deny', feedback)} />
              ))}
            </div>
          </div>
        )}

        {items.length === 0 && !loading && <div className="text-sm text-gray-600">No pending deployments.</div>}
        {items.map(it => (
          <div key={`mod-${it.module_id}-${it.request_id || 'na'}`} className="p-4 border rounded flex flex-col lg:flex-row gap-4 lg:justify-between lg:items-start">
            <div className="min-w-0">
              <div className="font-semibold break-words">{it.module_name} (Module ID: {it.module_id})</div>
              {it.requester_name || it.requester_email ? (
                <div className="text-sm text-gray-600">Requested by: {it.requester_name || it.requester_email}</div>
              ) : null}
              {(() => {
                const taskRunAt = it.auto_deploy_task_run_at;
                const taskStatus = (it.auto_deploy_task_status || '').toLowerCase();
                const hasRoster = Number(it.student_count || 0) > 0;

                if (taskRunAt) {
                  const asDate = new Date(taskRunAt);
                  const isValid = !Number.isNaN(asDate.getTime());
                  const displayDate = isValid
                    ? `${asDate.getFullYear()}-${String(asDate.getMonth() + 1).padStart(2, '0')}-${String(asDate.getDate()).padStart(2, '0')} ${String(asDate.getHours()).padStart(2, '0')}:${String(asDate.getMinutes()).padStart(2, '0')}`
                    : String(taskRunAt);
                  const statusLabel = taskStatus ? taskStatus.charAt(0).toUpperCase() + taskStatus.slice(1) : 'Queued';
                  return (
                    <>
                      <div className="text-sm text-gray-600">Deployment date: {displayDate}</div>
                      <div className="text-sm text-gray-600">Current status: {statusLabel}</div>
                    </>
                  );
                }

                if (hasRoster) {
                  return (
                    <>
                      <div className="text-sm text-amber-700">Deployment date: Enrolment was added after a past release date; manual sending required.</div>
                      <div className="text-sm text-amber-700">Current status: Manual deployment required</div>
                    </>
                  );
                }

                return (
                  <>
                    <div className="text-sm text-gray-600">Deployment date: Not set</div>
                    <div className="text-sm text-gray-600">Current status: Waiting for enrolments</div>
                  </>
                );
              })()}
              <div className="text-sm text-gray-600">Students enrolled: {it.student_count}</div>
              {(() => {
                const yrs = it.enrollment_years && it.enrollment_years.length ? it.enrollment_years.map(e=>e.year) : [];
                const sortedYears = yrs.slice().sort((a, b) => b - a);
                const latest = viewYear || (sortedYears.length ? sortedYears[0] : currentAcademicYear || null);
                return latest ? <div className="text-sm text-gray-700 mt-1">Target academic year: <span className="font-semibold">{latest}</span></div> : null;
              })()}
              {it.has_pending_request ? (
                <div className="text-sm text-yellow-700 mt-2"> The module leader{it.pending_requester_name ? `, ${it.pending_requester_name},` : ''} has proposed additions that are pending approval. These additions will be ignored unless approved.</div>
              ) : null}
            </div>
            <div className="w-full lg:w-auto flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2">
              {it.student_count === 0 ? (
                <>
                  <label className="text-xs text-gray-600">Upload roster</label>
                  <input type="file" onChange={(e) => e.target.files[0] && handleFileUpload(it.module_id, e.target.files[0])} className="p-1 max-w-full" />
                  <button
                    onClick={() => openRosterEditor(it)}
                    disabled={rosterLoading}
                    className="text-sm text-indigo-600 underline"
                  >
                    {rosterLoading ? 'Loading editor...' : 'Edit roster manually'}
                  </button>
                </>
              ) : (
                <div className="text-sm text-gray-700">
                  <div>Roster present ({it.student_count} students)</div>
                  {it.enrollments_sample && it.enrollments_sample.length > 0 ? (
                    <div className="text-xs text-gray-600 mt-1">Sample: {it.enrollments_sample.map(r => r.student_email).slice(0,3).join(', ')}</div>
                  ) : null}
                  <div className="mt-2">
                    <button
                      onClick={() => openRosterEditor(it)}
                      disabled={rosterLoading}
                      className="text-sm text-indigo-600 underline"
                    >
                      {rosterLoading ? 'Loading editor...' : 'Edit roster'}
                    </button>
                  </div>
                </div>
              )}

              <button disabled={actionLoading === (it.request_id || it.module_id) || it.student_count === 0} onClick={() => handleDeploy(it)} className={`px-3 py-2 rounded ${it.student_count===0 ? 'bg-gray-300 text-gray-600' : 'bg-indigo-600 text-white'}`}>
                {actionLoading === (it.request_id || it.module_id) ? 'Sending...' : `Send Now (early distribution)${(() => { const yrs = it.enrollment_years && it.enrollment_years.length ? it.enrollment_years.map(e=>e.year) : []; const sortedYears = yrs.slice().sort((a, b) => b - a); const latest = viewYear || (sortedYears.length ? sortedYears[0] : currentAcademicYear || null); return latest ? ` (for ${latest})` : '' })()}`}
              </button>
            </div>
          </div>
        ))}
      </div>
      <RosterEditorModal
        isOpen={isRosterEditorOpen}
        onClose={() => setIsRosterEditorOpen(false)}
        moduleName={rosterEditorTarget ? rosterEditorTarget.moduleName : 'Module'}
        year={rosterEditorTarget ? rosterEditorTarget.year : (viewYear || currentAcademicYear)}
        initialRows={rosterEditorRows}
        onSave={handleSaveRoster}
        isSaving={rosterSaving}
      />
    </div>
  );
};

export default PendingDeployments;
